import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, RefreshCw, Users } from 'lucide-react';
import { useSharedPermissions } from '../contexts/PermissionsContext';
import { useAuth0AdministrationApi, type Auth0Role, type Auth0User } from '../services/auth0AdministrationService';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import './Auth0PermissionsMatrix.css';

type Row = { roleIds: Set<string>; status: 'loading' | 'ready' | 'saving' | 'failed'; error?: string };
const message = (error: unknown) => error instanceof Error ? error.message : 'Unable to complete the Auth0 request.';
const userName = (user: Auth0User) => user.name || user.nickname || user.id;
const userRoleLabel = (user: Auth0User, role: Auth0Role) => `${userName(user)} (${user.id}): ${role.name}`;

export function Auth0UserRolesMatrix() {
  const { isAdmin } = useSharedPermissions();
  const api = useAuth0AdministrationApi();
  const [roles, setRoles] = useState<Auth0Role[]>([]);
  const [users, setUsers] = useState<Auth0User[]>([]);
  const [rows, setRows] = useState<Record<string, Row>>({});
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const refreshedVersion = useRef(0);
  const pendingWrites = useRef(new Set<string>());
  const pendingReads = useRef(new Set<string>());
  const queue = useRef({ active: 0, waiting: [] as (() => void)[] });
  const saving = Object.values(rows).some(row => row.status === 'saving');
  const readRoles = useCallback(async (id: string) => {
    const state = queue.current;
    if (state.active < 3) state.active++; else await new Promise<void>(resolve => state.waiting.push(resolve));
    try { return await api.getUserRoles(id); }
    finally { const next = state.waiting.shift(); if (next) next(); else state.active--; }
  }, [api]);

  useEffect(() => {
    if (saving || search === query) return;
    const timer = setTimeout(() => {
      if (pendingWrites.current.size === 0) { setQuery(search); setPage(0); }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, query, saving]);

  useEffect(() => {
    if (!isAdmin) return;
    const lifecycle = generation;
    const version = ++lifecycle.current;
    const shouldRefresh = refresh !== refreshedVersion.current;
    refreshedVersion.current = refresh;
    setLoading(true); setExportLoading(false); setError(''); setUsers([]); setRows({});
    const current = () => version === lifecycle.current;
    const load = async () => {
      try {
        const capability = await api.capabilities();
        if (!current()) return;
        if (!capability.configured) { setError(capability.message || 'Auth0 administration is not configured.'); return; }
        const [nextRoles, initialPage] = await Promise.all([api.roles(), api.users(page, query, shouldRefresh)]);
        if (!current()) return;
        setRoles([...nextRoles].sort((a, b) => a.name.localeCompare(b.name)));
        let userPage = initialPage;
        while (userPage.loading && current()) {
          setExportLoading(true);
          await new Promise(resolve => setTimeout(resolve, 3000));
          if (!current()) return;
          userPage = await api.users(page, query);
        }
        if (!current()) return;
        setExportLoading(false);
        if (userPage.error) { setError(userPage.error); return; }
        setTotal(userPage.total);
        if (page > 0 && userPage.items.length === 0 && userPage.total > 0) {
          setPage(Math.max(0, Math.ceil(userPage.total / 25) - 1)); return;
        }
        setUsers(userPage.items);
        if (nextRoles.length === 0) return;
        setRows(Object.fromEntries(userPage.items.map(user => [user.id, { roleIds: new Set<string>(), status: 'loading' }])));
        let index = 0;
        const worker = async () => {
          while (index < userPage.items.length && current()) {
            const user = userPage.items[index++];
            try {
              const assigned = await readRoles(user.id);
              if (current()) setRows(previous => ({ ...previous, [user.id]: { roleIds: new Set(assigned.map(role => role.id)), status: 'ready' } }));
            } catch (e) {
              if (current()) setRows(previous => ({ ...previous, [user.id]: { roleIds: new Set<string>(), status: 'failed', error: message(e) } }));
            }
          }
        };
        await Promise.all(Array.from({ length: Math.min(3, userPage.items.length) }, worker));
      } catch (e) { if (current()) setError(message(e)); }
      finally { if (current()) setLoading(false); }
    };
    void load();
    return () => { lifecycle.current++; };
  }, [api, isAdmin, page, query, readRoles, refresh]);

  const retry = async (user: Auth0User) => {
    if (pendingReads.current.has(user.id) || pendingWrites.current.has(user.id)) return;
    pendingReads.current.add(user.id);
    const version = generation.current;
    setRows(previous => ({ ...previous, [user.id]: { ...previous[user.id], status: 'loading', error: undefined } }));
    try {
      const assigned = await readRoles(user.id);
      if (version === generation.current) setRows(previous => ({ ...previous, [user.id]: { roleIds: new Set(assigned.map(role => role.id)), status: 'ready' } }));
    } catch (e) {
      if (version === generation.current) setRows(previous => ({ ...previous, [user.id]: { ...previous[user.id], status: 'failed', error: message(e) } }));
    } finally { pendingReads.current.delete(user.id); }
  };

  const change = async (user: Auth0User, role: Auth0Role, assigned: boolean) => {
    if (rows[user.id]?.status !== 'ready' || pendingWrites.current.has(user.id)) return;
    pendingWrites.current.add(user.id);
    const version = generation.current;
    setRows(previous => ({ ...previous, [user.id]: { ...previous[user.id], status: 'saving', error: undefined } }));
    let saveError = '';
    try { await api.changeUserRole(user.id, role.id, assigned); }
    catch (e) { saveError = `${message(e)} The change may have completed.`; }
    try {
      const actual = await readRoles(user.id);
      if (version === generation.current) setRows(previous => ({ ...previous, [user.id]: {
        roleIds: new Set(actual.map(value => value.id)), status: 'ready', error: saveError ? `${saveError} Actual roles have been reloaded.` : undefined,
      } }));
    } catch (e) {
      if (version === generation.current) setRows(previous => ({ ...previous, [user.id]: { ...previous[user.id], status: 'failed',
        error: `${saveError || 'The save outcome could not be confirmed.'} ${message(e)} Retry to reload this user before editing.` } }));
    } finally { pendingWrites.current.delete(user.id); }
  };
  const refreshAll = () => { if (pendingWrites.current.size === 0) setRefresh(value => value + 1); };
  const changePage = (next: number) => { if (pendingWrites.current.size === 0) setPage(next); };
  const headerHeight = Math.max(160, ...roles.map(role => role.name.length * 9 + 50));
  const loaded = Object.values(rows).filter(row => row.status !== 'loading').length;

  if (!isAdmin) return <Alert><AlertDescription>Only administrators can assign user roles.</AlertDescription></Alert>;
  return <section className="auth0-matrix-card" data-analytics-private="true" aria-label="Auth0 user roles matrix">
    <div className="auth0-matrix-toolbar"><div><h2><Users size={18} aria-hidden="true" />Auth0 User Roles Matrix</h2><p>Click a checkbox to assign or remove a user’s direct role. Changes are saved in Auth0.</p></div>
      <Button variant="outline" disabled={saving || loading} onClick={refreshAll}><RefreshCw className="size-4 mr-2" />Refresh</Button></div>
    <p className="auth0-matrix-note">Updated access applies to newly issued tokens; existing sessions may need to sign in again.</p>
    <Input aria-label="Search users" placeholder="Search by name, email, or Auth0 ID" maxLength={200} value={search} disabled={saving} onChange={e => setSearch(e.target.value)} className="mb-4" />
    {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription><Button variant="outline" disabled={saving} onClick={refreshAll}>Retry</Button></Alert>}
    {loading && <p role="status">{exportLoading ? 'Loading complete user directory…' : users.length > 0 && roles.length > 0 ? `Loading user roles: ${loaded} of ${users.length}` : 'Loading Auth0 user-role matrix…'}</p>}
    {!loading && !error && roles.length === 0 && <p>No roles exist yet. Create a role in the Roles tab.</p>}
    {!loading && !error && users.length === 0 && <p>No users found.</p>}
    {users.length > 0 && roles.length > 0 && <div className="auth0-matrix-scroll" tabIndex={0} aria-label="Scrollable user and role assignments">
      <table className="auth0-matrix-table" style={{ width: `calc(var(--permission-width) + ${roles.length * 56}px)` }}>
        <colgroup><col className="auth0-matrix-permission-col" />{roles.map(role => <col key={role.id} style={{ width: 56 }} />)}</colgroup>
        <thead><tr style={{ height: headerHeight }}><th scope="col" className="auth0-matrix-corner">User</th>{roles.map(role => <th scope="col" key={role.id} className="auth0-matrix-role-header" aria-label={role.name}>
          <Tooltip><TooltipTrigger asChild><span className="auth0-matrix-role-name" tabIndex={0}>{role.name}</span></TooltipTrigger><TooltipContent>{role.name}{role.description && ` — ${role.description}`}</TooltipContent></Tooltip>
        </th>)}</tr></thead>
        <tbody>{users.map(user => <tr key={user.id}><th scope="row" className="auth0-matrix-label">
          <span className="auth0-matrix-user-name">{userName(user)}{user.blocked && <small className="auth0-matrix-blocked">Blocked</small>}</span>
          {user.email && <small className="auth0-matrix-user-detail">{user.email}</small>}<small className="auth0-matrix-user-detail">{user.id}</small>
          {(rows[user.id]?.status === 'loading' || rows[user.id]?.status === 'saving') && <span className="auth0-matrix-user-status" role="status"><Loader2 size={12} className="animate-spin" />{rows[user.id].status === 'saving' ? 'Saving…' : 'Loading…'}</span>}
          {rows[user.id]?.error && <div className="auth0-matrix-user-error" role="alert">{rows[user.id].error}<Button variant="outline" size="sm" disabled={rows[user.id].status === 'loading' || rows[user.id].status === 'saving'} onClick={() => void retry(user)}>Retry {userName(user)}</Button></div>}
        </th>{roles.map(role => <td key={role.id} className="auth0-matrix-cell"><Checkbox checked={rows[user.id]?.roleIds.has(role.id) ?? false} disabled={rows[user.id]?.status !== 'ready'} aria-label={userRoleLabel(user, role)} onCheckedChange={checked => void change(user, role, checked === true)} /></td>)}</tr>)}</tbody>
      </table>
    </div>}
    {!exportLoading && !error && <div className="auth0-matrix-pagination"><Button variant="outline" disabled={saving || loading || page === 0} onClick={() => changePage(page - 1)}>Previous</Button><span>Page {page + 1} · {total} users</span><Button variant="outline" disabled={saving || loading || (page + 1) * 25 >= total} onClick={() => changePage(page + 1)}>Next</Button></div>}
  </section>;
}
